import { NgClass, NgIf, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AbstractItemComponent } from '@df/catalog-ui/abstract-item.component';
import { Listing } from '@df/catalog-ui/listing/listing';
import { IPageDataV2, IPageDataV3 } from '@df/catalog/cms/cms-model.interface';
import { App } from '@df/core/app';
import { StickyNativeDirective } from '@df/legacy/ui/sticky/sticky-native.directive';
import { TimeoutService } from '@df/ng/timeout.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { TranslationService } from '@df/ui/translation/translation.service';
import { ICmsPage } from '@icms/core/resource/page/icms-page';
import { Subscription } from 'rxjs';
import sortBy from 'lodash-es/sortBy';
import { SwiperModule } from 'swiper/angular';
import { LocalPage } from '../../../page.decorator';
import { NBlogCategoryPageType } from '../../page/category-view/cms-blog-page-category-view.component';
import { NBlogPostPageType } from '../../page/post-view/cms-blog-post.namespace';
import { CmsBlogContentBlogCardComponent } from '../blog-card/cms-blog-content-blog-card.component';
import { CmsBlogContentTagFilter } from '../tag-filter/cms-blog-content-tag-filter';

interface INavigationItem {
    url: string;
    name: string;
    id?: number;
}

@Component({
    selector: 'cms-blog-content-post-listing',
    templateUrl: './cms-blog-content-post-listing.html',
    styleUrls: ['./cms-blog-content-post-listing.scss'],
    standalone: true,
    imports: [
        CmsBlogContentBlogCardComponent,
        GridModule,
        NgClass,
        NgIf,
        RouterLink,
        StickyNativeDirective,
        SwiperModule,
        TranslationModule,
        UpperCasePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CmsBlogContentPostListingViewComponent extends AbstractItemComponent<LocalPage<IPageDataV3<NBlogCategoryPageType.ITypeData>>> {
    type = ICmsPage.type;

    /**
     * Listing subscfiptions
     */
    private listingSubscription?: Subscription;

    protected isNavMenuOpen = false;
    /**
     * Children posts listing
     */
    listing?: Listing<LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>>;

    /**
     * Blog page origin
     */
    readonly blogOrigin = NBlogCategoryPageType.blogOrigin;

    constructor(
        changeDetectorRef: ChangeDetectorRef,
        app: App,
        timeoutService: TimeoutService,
        private translationService: TranslationService
    ) {
        super(changeDetectorRef, app, timeoutService);
    }
    /**
     * Type data getter shorthand
     */
    get typeData() {
        return this.model?.data?.typeData;
    }

    /**
     * Parent page type data shorthand
     */
    private get parentTypeData() {
        return (this.model?.parent?.data as IPageDataV3<NBlogCategoryPageType.ITypeData>)?.typeData;
    }
    /**
     * Sibling categories
     */
    private siblingsOrChildren: LocalPage<IPageDataV2>[] = [];

    /**
     * Setup listing on model updates
     */

    protected override onModelChange(): void {
        super.onModelChange();
        this.listingSubscription?.unsubscribe();
        this.listing?.clearSubscriptions();
        if (this.model) {
            this.listing = new Listing<LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>>(
                {
                    type: 'page',
                    storage: true,
                    infScrollInit: [12, 16, 16, 16],
                    infScrollStep: [12, 16, 16, 16]
                },
                sortBy(this.model.getBlogCategoryPosts() as LocalPage<IPageDataV2<NBlogPostPageType.ITypeData>>[], model => {
                    const date = model.data?.typeData?.date || model.data?.createdAt;
                    if (date) {
                        const dateObject = new Date(date);
                        return -dateObject.getTime() || 0;
                    }
                    return 0;
                })
            );
            this.listing.addFilter(new CmsBlogContentTagFilter({ id: 'tags', isOpen: true, position: 0, type: 'custom' }, this.listing));
            this.listing.applyFilters();
            const parentId = this.model?.data?.parent || this.model?.id;
            this.siblingsOrChildren = this.app.resources.page
                .getAllExisting()
                .filter(i => i.data?.pageType === NBlogCategoryPageType.typeId && i.data?.parent === parentId) as LocalPage<IPageDataV2>[];
            this.toggleNavMenu(false, true);
            this.listing.subscribe(() => this.detectChanges());
        }
    }

    get navigationItems(): INavigationItem[] {
        return [
            { name: this.translationService.translate('All articles'), url: this.blogOrigin },
            ...(this.typeData?.tagRedirect ? this.siblingsOrChildren : this.tagFilter.options).map(i => ({
                name: i.name,
                url: i.data?.url || '',
                id: i.id
            }))
        ];
    }

    get currentCategoryName(): string {
        const item = this.navigationItems.find(item => this.isSelected(item));
        return (item?.id && item?.name) || this.translationService.translate('All articles');
    }

    get tagFilter() {
        return this.listing?.filtersMap['tags'] as CmsBlogContentTagFilter;
    }

    get name() {
        return this.typeData?.useParent ? this.model?.parent?.data?.name : this.model?.data?.name;
    }

    get topCopy() {
        return this.typeData?.useParent ? this.parentTypeData?.topCopy : this.typeData?.topCopy;
    }

    /**
     * No option passed for 'view all'
     */
    isSelected(option: INavigationItem): boolean {
        if (this.typeData?.tagRedirect) {
            return option.id ? option.id === this.model?.id : this.model?.data?.url === NBlogCategoryPageType.blogOrigin;
        }
        return option.id ? this.tagFilter.isSelected(option.id) : !this.tagFilter.hasSelection;
    }

    /**
     * No option passed for 'view all'
     */
    selectNav(option: INavigationItem) {
        if (!this.typeData?.tagRedirect) {
            if (option.id) {
                this.tagFilter.select(option.id);
            } else {
                this.tagFilter.reset();
            }
        }
        this.toggleNavMenu(false);
    }

    toggleNavMenu(to = !this.isNavMenuOpen, silent = false) {
        if (this.isNavMenuOpen !== to) {
            this.isNavMenuOpen = to;
            if (!silent) {
                this.detectChanges();
            }
        }
    }
}
